import React, { useState, useCallback, useRef, useEffect } from 'react';
import './DateRangePicker.css';

const DateRangePicker = ({ startDate, endDate, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [currentMonth, setCurrentMonth] = useState(startDate || new Date());
  const dropdownRef = useRef(null);

  const months = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  const quickRanges = [
    {
      label: 'Сегодня',
      getRange: () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return { start: today, end: today };
      }
    },
    {
      label: 'Неделя',
      getRange: () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startOfWeek = new Date(today);
        const dayOfWeek = startOfWeek.getDay();
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(startOfWeek.getDate() + daysToMonday);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        return { start: startOfWeek, end: endOfWeek };
      }
    },
    {
      label: 'Месяц',
      getRange: () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { start: startOfMonth, end: endOfMonth };
      }
    },
    {
      label: 'Прошлая неделя',
      getRange: () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startOfLastWeek = new Date(today);
        const dayOfWeek = startOfLastWeek.getDay();
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfLastWeek.setDate(startOfLastWeek.getDate() + daysToMonday - 7);
        
        const endOfLastWeek = new Date(startOfLastWeek);
        endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
        
        return { start: startOfLastWeek, end: endOfLastWeek };
      }
    },
    {
      label: 'Прошлый месяц',
      getRange: () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: startOfLastMonth, end: endOfLastMonth };
      }
    }
  ];

  // Получение текущей даты (без времени)
  const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Генерация дней месяца
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startDate = new Date(firstDay);
    const dayOfWeek = startDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startDate.setDate(startDate.getDate() + daysToMonday);
    
    const days = [];
    const today = getToday();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = date.getMonth() === month;
      const isToday = date.toDateString() === today.toDateString();
      const isStartDate = tempStartDate && date.toDateString() === tempStartDate.toDateString();
      const isEndDate = tempEndDate && date.toDateString() === tempEndDate.toDateString();
      const isInRange = tempStartDate && tempEndDate && date >= tempStartDate && date <= tempEndDate;
      
      days.push({
        date,
        day: date.getDate(),
        isCurrentMonth,
        isToday,
        isStartDate,
        isEndDate,
        isInRange
      });
    }
    
    return days;
  };

  const handleDateSelect = (date) => {
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Начало нового диапазона
      setTempStartDate(date);
      setTempEndDate(null);
    } else if (date >= tempStartDate) {
      // Завершение диапазона
      setTempEndDate(date);
    } else {
      // Если выбрана дата раньше начальной, меняем начальную дату
      setTempStartDate(date);
      setTempEndDate(null);
    }
  };

  const applyDateRange = () => {
    if (tempStartDate && tempEndDate) {
      onChange(tempStartDate, tempEndDate);
      setIsOpen(false);
    }
  };

  const handleQuickSelect = useCallback((rangeGetter) => {
    const { start, end } = rangeGetter();
    setTempStartDate(start);
    setTempEndDate(end);
    onChange(start, end);
    setIsOpen(false);
  }, [onChange]);

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const formatDate = useCallback((date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }, []);

  // Сброс временных дат при открытии/закрытии
  useEffect(() => {
    if (isOpen) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [isOpen, startDate, endDate]);

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const days = getDaysInMonth();

  return (
    <div className="date-range-picker" ref={dropdownRef}>
      <button className="range-display" onClick={() => setIsOpen(!isOpen)}>
        <i className="fas fa-calendar-alt"></i>
        {formatDate(startDate)} - {formatDate(endDate)}
        <i className={`fas fa-chevron-down ${isOpen ? 'open' : ''}`}></i>
      </button>
      
      {isOpen && (
        <div className="range-dropdown">

          
          <div className="custom-range">

            <div className="calendar-container">
              <div className="calendar-header">
                <button className="nav-button" onClick={goToPreviousMonth} type="button">
                  <i className="fas fa-chevron-left"></i>
                </button>
                
                <div className="current-month">
                  {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </div>
                
                <button className="nav-button" onClick={goToNextMonth} type="button">
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>

              <div className="calendar-weekdays">
                {weekDays.map(day => (
                  <div key={day} className="calendar-weekday-bot">{day}</div>
                ))}
              </div>

              <div className="calendar-days-grid">
                {days.map((day, index) => (
                  <div
                    key={index}
                    className={`calendar-grid-day ${
                      !day.isCurrentMonth ? 'calendar-other-month' : ''
                    } ${day.isToday ? 'calendar-today' : ''} ${
                      day.isStartDate ? 'calendar-start-date' : ''
                    } ${day.isEndDate ? 'calendar-end-date' : ''} ${
                      day.isInRange ? 'calendar-in-range' : ''
                    }`}
                    onClick={() => handleDateSelect(day.date)}
                  >
                    {day.day}
                  </div>
                ))}
              </div>

              <div className="calendar-footer">
                
                <button 
                  className="exchange-btn"
                  onClick={applyDateRange}
                  disabled={!tempStartDate || !tempEndDate}
                >
                  Применить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;